import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import LocaleRoute from '@/components/LocaleRoute';
import { MonitoringProvider } from '@/components/MonitoringProvider';

// Core pages (loaded immediately)
import HomePage from '@/pages/HomePage';

// Core pages (lazy loaded) - split to reduce initial bundle
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage'));
const DocuSignVerifyPage = lazy(() => import('@/pages/docusign/VerifyPage'));
const DocuSignCompletePage = lazy(() => import('@/pages/docusign/CompletePage'));
const IBANCollectionPage = lazy(() => import('@/pages/banking/IBANCollectionPage'));

// Non-core pages (lazy loaded)
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const UpdatesPage = lazy(() => import('@/pages/UpdatesPage'));

const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const InvestorPrivacyPage = lazy(() => import('@/pages/InvestorPrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const ImpressumPage = lazy(() => import('@/pages/ImpressumPage'));
const FacebookPrivacyPage = lazy(() => import('@/pages/FacebookPrivacyPage'));
const SocialCallback = lazy(() => import('@/pages/auth/SocialCallback'));
const DataDeletionPage = lazy(() => import('@/pages/DataDeletionPage'));

import ScrollToTop from '@/components/ScrollToTop';

function App() {
  const { i18n } = useTranslation();
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication status on app load
    // This validates the stored token is still valid
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    // Handle locale routing
    const path = location.pathname;
    const localeMatch = path.match(/^\/([a-z]{2})(\/|$)/);
    const currentLocale = i18n.language || 'de';

    if (!localeMatch && !path.startsWith('/auth/callback')) {
      // No locale in path, redirect to default locale
      // Exception: auth callback which is locale-agnostic initially
      navigate(`/${currentLocale}${path}`, { replace: true });
    } else {
      const locale = localeMatch ? localeMatch[1] : null;
      const supportedLocales = i18n.languages || ['de'];

      if (locale && supportedLocales.includes(locale)) {
        // Valid locale in URL - switch to it if different
        if (i18n.language !== locale) {
          i18n.changeLanguage(locale);
        }
      } else if (locale && !supportedLocales.includes(locale)) {
        // Invalid locale in URL - redirect to German
        const pathWithoutLocale = path.replace(/^\/[a-z]{2}(\/|$)/, '/');
        navigate(`/de${pathWithoutLocale}`, { replace: true });
      }
    }
  }, [location.pathname, i18n, navigate]);

  return (
    <MonitoringProvider>
      <ScrollToTop />
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>}>
        <Routes>
        {/* Social Auth Callback - Locale agnostic */}
        <Route path="/auth/callback" element={<SocialCallback />} />

        {/* Redirect root to default locale */}
        <Route path="/" element={<Navigate to={`/${i18n.language || 'de'}`} replace />} />

        {/* Locale-specific routes */}
        <Route path="/:locale" element={<LocaleRoute />}>
          <Route element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="auth/login" element={<LoginPage />} />
            <Route path="auth/login" element={<LoginPage />} />
            <Route path="updates" element={<UpdatesPage />} />
            <Route path="about" element={<AboutPage />} />

            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="investor-privacy" element={<InvestorPrivacyPage />} />
            <Route path="facebook-privacy" element={<FacebookPrivacyPage />} />
            <Route path="datenschutz-facebook" element={<FacebookPrivacyPage />} />
            <Route path="terms" element={<TermsPage />} />
            <Route path="imprint" element={<ImpressumPage />} />
            <Route path="data-deletion" element={<DataDeletionPage />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="docusign/verify" element={<DocuSignVerifyPage />} />
              <Route path="docusign/complete" element={<DocuSignCompletePage />} />
              <Route path="banking/iban" element={<IBANCollectionPage />} />
            </Route>
          </Route>
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </MonitoringProvider>
  );
}

export default App;